import React, { useCallback, useState } from 'react';
import { getUnsavedWork } from 'src/store/workspace/unsavedWork';
import DiscardChangesDialog, {
  UnsavedTabsSummary
} from '../pedigree-sidebar/DiscardChangesDialog';

/**
 * Ask before an action that tears the whole workspace down would discard the
 * unsaved sidebar edits held in any open tab: logging out, and switching user
 * group (a page reload). Used from the app bar, which renders outside the
 * WorkspaceProvider and so reads what the provider publishes (see
 * src/store/workspace/unsavedWork.js).
 *
 *   requestLeave(proceed, { confirmLabel })
 *     - no tab holds unsaved edits: `proceed` runs at once, exactly as before;
 *     - otherwise the shared DiscardChangesDialog opens, naming the tabs and
 *       their sections the way the tab strip's close guard does. "Keep editing"
 *       (the default, and Escape) drops `proceed`; the destructive button runs
 *       it. There is nothing to discard tab by tab first: the teardown itself
 *       unmounts every sidebar, and each one reports nothing unsaved as it goes.
 *
 * Render `leaveDialog` OUTSIDE any menu the action lives in: closing the menu
 * unmounts its children, and the prompt with them.
 *
 * A forced logout (session expiry, the inactivity timer) cannot ask anyone
 * anything and does not come through here.
 *
 * @returns {{ requestLeave: Function, leaveDialog: React.ReactNode }}
 */
export default function useUnsavedWorkGuard() {
  // { open, proceed, confirmLabel, tabs }. Kept when the dialog closes so its
  // text does not change while it fades out.
  const [pending, setPending] = useState(null);

  const requestLeave = useCallback(
    (proceed, { confirmLabel = 'Discard and leave' } = {}) => {
      const tabs = getUnsavedWork();
      if (!tabs.length) {
        proceed();
        return true;
      }
      setPending({ open: true, proceed, confirmLabel, tabs });
      return false;
    },
    []
  );

  const close = () => setPending((p) => (p ? { ...p, open: false } : p));

  const handleConfirm = () => {
    if (!pending?.open) return;
    const { proceed } = pending;
    close();
    proceed();
  };

  const leaveDialog = (
    <DiscardChangesDialog
      open={Boolean(pending?.open)}
      onCancel={close}
      onConfirm={handleConfirm}
      cancelLabel="Keep editing"
      confirmLabel={pending?.confirmLabel || 'Discard'}
      ariaLabel="Confirm leaving with unsaved changes"
    >
      <UnsavedTabsSummary sessions={pending?.tabs || []} />
    </DiscardChangesDialog>
  );

  return { requestLeave, leaveDialog };
}
